import { useEffect, useState } from "react";
import BandDeco from "../candidate/BandDeco";
import { useRouteLoaderData, useSearchParams } from "react-router-dom";
import { User } from "../../services/user/userModel";
import IdentitySection from "./IdentitySection";
import PasswordSection from "./PasswordSection";
import PremiumSection from "./PremiumSection";
import PremiumSponsorshipSection from "./PremiumSponsorshipSection";
import { useTranslation } from "react-i18next";
import { PremiumSponsorship } from "../../services/premiumSponsorship/premiumSponsorshipModel";
import { premiumSponsorshipService } from "../../services/premiumSponsorship/premiumSponsorshipService";
import { toastsWithIntl } from "../../services/toastService";

const { getMyPremiumSponsorships } = premiumSponsorshipService();
const { toastError } = toastsWithIntl(["accounts"]);

const AccountPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser: User;
  };

  const isOrganization = loggedUser.organizationId !== null;
  const [mySponsorships, setMySponsorships] = useState<PremiumSponsorship[]>(
    [],
  );

  const reloadSponsorships = () => {
    if (isOrganization) return;
    getMyPremiumSponsorships()
      .then(setMySponsorships)
      .catch(() => undefined);
  };

  useEffect(() => {
    reloadSponsorships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrganization]);

  // Set by the login form when the candidate followed a sponsorship
  // invitation but signed in with another one of their addresses: the
  // sponsorship stayed untouched server-side, which is otherwise silent.
  const mismatchedSponsorshipEmail = searchParams.get(
    "sponsorshipEmailMismatch",
  );
  useEffect(() => {
    if (!mismatchedSponsorshipEmail) return;
    toastError("accounts:premium-sponsorship.accept.EMAIL_MISMATCH", {
      replace: { email: mismatchedSponsorshipEmail },
      id: "sponsorship-email-mismatch",
    });
    setSearchParams(
      (prev) => {
        prev.delete("sponsorshipEmailMismatch");
        return prev;
      },
      { replace: true },
    );
  }, [mismatchedSponsorshipEmail, setSearchParams]);

  const hasActiveSponsorshipGrant = mySponsorships.some(
    (sponsorship) =>
      sponsorship.status === "accepted" && sponsorship.premiumGranted === true,
  );
  const displayPremium =
    !isOrganization &&
    loggedUser.subscriptionEndDate &&
    !hasActiveSponsorshipGrant;

  const sections = (
    <div className="ui-grid --g2 cand-account">
      <IdentitySection user={loggedUser} />
      <div className="cand-stack">
        <PasswordSection />
        {displayPremium && <PremiumSection user={loggedUser} />}
        {!isOrganization && (
          <PremiumSponsorshipSection
            sponsorships={mySponsorships}
            onChange={reloadSponsorships}
          />
        )}
      </div>
    </div>
  );

  // Candidat : bandeau orange de la maquette ; professionnel : en-tête de page sur fond clair.
  return isOrganization ? (
    <div className="layout_content --bg--organization-dashboard">
      <div className="ui-page cand-account_page">
        <div className="ui-ph cand-account_head">
          <div className="ui-ph_t">
            <h1>{t("common:header.MY_ACCOUNT")}</h1>
            <p>{loggedUser.email}</p>
          </div>
        </div>
        {sections}
      </div>
    </div>
  ) : (
    <div className="layout_content cand-page">
      <section className="ui-band cand-band">
        <BandDeco />
        <div className="ui-band_in cand-band_in">
          <h1>{t("common:header.MY_ACCOUNT")}</h1>
          <p className="ui-band_sub">{loggedUser.email}</p>
        </div>
      </section>
      <div className="ui-page">{sections}</div>
    </div>
  );
};

export default AccountPage;
